import { Landmark } from 'lucide-react';
import { govOf, type GovContent, type GovPolicy } from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, type NumberSpec } from './form';

type GovNumbers = Exclude<keyof GovContent, 'visible' | 'policies'>;
const RULES: NumberSpec<GovNumbers>[] = [
  { key: 'standFeePence', label: 'Standing for Mayor costs', unit: 'pounds', min: 0, max: 1_000_000 },
  { key: 'mayorPayPence', label: 'The Mayor is paid each London day', unit: 'pounds', min: 0, max: 100_000 },
  {
    key: 'ageDays',
    label: 'A life votes and stands once it is this many days old',
    unit: 'number',
    min: 0,
    max: 60,
    whole: true,
  },
  {
    key: 'policyHours',
    label: 'The Mayor changes the policy at most every (hours)',
    unit: 'number',
    min: 1,
    max: 168,
    whole: true,
  },
  {
    key: 'announceWaitHours',
    label: 'After a take-down, the Mayor waits (hours)',
    unit: 'number',
    min: 1,
    max: 168,
    whole: true,
  },
  {
    key: 'reportsToHide',
    label: 'Reports that hide an announcement until the team looks',
    unit: 'number',
    min: 1,
    max: 50,
    whole: true,
  },
];
const PERCENT: NumberSpec = { key: 'percent', label: 'How much (%)', unit: 'number', min: 1, max: 90, whole: true };

// Gov (as Lagos Life's 🏛️ Gov): the Mayor's election and office. What standing costs and the Mayor earns, who can
// vote, how often the policy changes, and the policies themselves. Part of the content draft, published with it.
// The race, the Mayor and the announcements are under Players → Gov.
export function GovPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const gov = govOf(working ?? {});
  const change = (fn: (draft: GovContent) => void) =>
    editor.edit((content) => {
      content.gov ??= structuredClone(govOf(content));
      fn(content.gov);
    });
  const policy = (id: GovPolicy['id'], fn: (draft: GovPolicy) => void) =>
    change((draft) => {
      const found = draft.policies.find((item) => item.id === id);
      if (found) fn(found);
    });
  return (
    <section>
      <PageHeader
        title="Gov"
        description="London's weekly Mayor election (as Lagos Life's Gov): what standing costs, the Mayor's pay, who can vote, and the policies the Mayor picks from. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">
              <Landmark size={16} aria-hidden="true" /> The rules
            </h2>
            <label className="designer-check">
              <Switch
                checked={gov.visible}
                aria-label="Gov open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can vote and stand
            </label>
            <p className="admin-muted">
              Polls close every Sunday at 8pm London time. Votes are cast in person at City Hall. A tie goes to whoever
              reached the total first.
            </p>
            <div className="content-grid">
              {RULES.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`gov-${spec.key}`}
                  spec={spec}
                  value={gov[spec.key]}
                  onChange={(value) => change((draft) => void (draft[spec.key] = value))}
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Policies">
            <h2 className="content-subtitle">The Mayor's policies</h2>
            <p className="admin-muted">
              One is in force at a time. Fares (bus, DLR, Tube, cable car), food and rent come down by the amount; wages
              and fun go up by it.
            </p>
            {gov.policies.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`gov-policy-${item.id}`}>
                <Field id={`gov-${item.id}-label`} label="Name">
                  <Input
                    id={`gov-${item.id}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => policy(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`gov-${item.id}-effect`} label="What players are told it does">
                  <Input
                    id={`gov-${item.id}-effect`}
                    value={item.effect}
                    maxLength={120}
                    onChange={(e) => policy(item.id, (entry) => void (entry.effect = e.target.value))}
                  />
                </Field>
                <Field id={`gov-${item.id}-icon`} label="Emoji">
                  <Input
                    id={`gov-${item.id}-icon`}
                    value={item.icon}
                    maxLength={8}
                    onChange={(e) => policy(item.id, (entry) => void (entry.icon = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`gov-${item.id}-percent`}
                  spec={PERCENT}
                  value={item.percent}
                  onChange={(value) => policy(item.id, (entry) => void (entry.percent = value))}
                />
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on offer`}
                    onCheckedChange={(on) => policy(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  On offer
                </label>
              </div>
            ))}
          </fieldset>
        </div>
      )}
    </section>
  );
}
